import { useState, useEffect, useCallback } from 'react'
import { 
  AdminDashboardState, 
  AdminStats
} from '@/types/admin'
import { getApiUrl } from '@/lib/apiUrl'

interface UseAdminDataProps {
  userId: string
}

export function useAdminData({ userId }: UseAdminDataProps) {
  const [state, setState] = useState<AdminDashboardState>({
    users: [],
    properties: [],
    applications: [],
    payments: [],
    paymentSummary: null,
    loading: {
      users: true,
      properties: true,
      applications: true,
      payments: true,
      paymentSummary: true,
    },
    error: null,
    stats: {
      totalUsers: 0,
      totalProperties: 0,
      verifiedProperties: 0,
      pendingProperties: 0,
      kycApplications: 0,
      pendingKycApplications: 0,
      approvedKycApplications: 0,
      verifiedUsers: 0,
      unverifiedUsers: 0,
      landlords: 0,
      clients: 0,
      totalRevenue: 0,
      totalCommission: 0,
      platformCommission: 0,
      verificationRate: 0,
      propertyVerificationRate: 0,
    }
  })

  // Load users
  const loadUsers = useCallback(async () => {
    if (!userId) return

    setState(prev => ({ ...prev, loading: { ...prev.loading, users: true }, error: null }))

    try {
      const token = localStorage.getItem('authToken')
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error(`Failed to load users: ${response.statusText}`)
      }
      const data = await response.json()

      // Transform users to match frontend format
      const transformedUsers = (data.users || []).map((user: any) => ({
        ...user,
        id: user._id || user.id,
        // Map createdAt to created_at for frontend compatibility
        created_at: user.createdAt || user.created_at || user.createdAt,
        is_verified: user.isVerified || user.is_verified || user.kyc?.status === 'verified',
        // Include profile and phone data in kyc_data for easy access
        kyc_data: {
          ...(user.kyc_data || {}),
          // Map profile data to kyc_data for backward compatibility
          firstName: user.firstName || user.kyc_data?.firstName,
          lastName: user.lastName || user.kyc_data?.lastName,
          phone: user.phone || user.kyc_data?.phone || user.profile?.phone,
          dateOfBirth: user.profile?.dateOfBirth || user.kyc_data?.dateOfBirth,
          address: user.profile?.address ? 
            `${user.profile.address.street || ''}, ${user.profile.address.city || ''}, ${user.profile.address.state || ''}`.trim().replace(/^,\s*|,\s*$/g, '') :
            user.kyc_data?.address,
          // Transform kyc.documents array to kyc_data.documents object
          // Handle income_proof by mapping to both bankStatement and employmentLetter if both exist
          documents: user.kyc_data?.documents || (user.kyc?.documents ? (() => {
            const acc: any = {}
            const incomeProofDocs: any[] = []
            
            user.kyc.documents.forEach((doc: any) => {
              if (doc.type === 'income_proof') {
                incomeProofDocs.push(doc)
              } else {
                const docTypeMap: Record<string, string> = {
                  'id': 'idDocument',
                  'proof_of_address': 'proofOfAddress',
                  'bank_statement': 'bankStatement',
                  'employment_letter': 'employmentLetter'
                }
                const mappedType = docTypeMap[doc.type] || doc.type
                // Only add if it's one of the expected types
                if (['idDocument', 'proofOfAddress', 'bankStatement', 'employmentLetter'].includes(mappedType) || 
                    ['id', 'proof_of_address', 'bank_statement', 'employment_letter'].includes(doc.type)) {
                  acc[mappedType] = doc.url
                }
              }
            })
            
            // Handle income_proof documents - map first to bankStatement, second to employmentLetter
            if (incomeProofDocs.length > 0) {
              if (!acc.bankStatement && incomeProofDocs[0]) {
                acc.bankStatement = incomeProofDocs[0].url
              }
              if (!acc.employmentLetter && incomeProofDocs[1]) {
                acc.employmentLetter = incomeProofDocs[1].url
              } else if (!acc.employmentLetter && incomeProofDocs[0] && acc.bankStatement !== incomeProofDocs[0].url) {
                // If we only have one income_proof and it's already mapped to bankStatement, 
                // check if there's a separate employment_letter document
                const hasEmploymentLetter = user.kyc.documents.some((d: any) => d.type === 'employment_letter')
                if (!hasEmploymentLetter) {
                  acc.employmentLetter = incomeProofDocs[0].url
                }
              }
            }
            
            return acc
          })() : {}),
          verification_status: user.kyc_data?.verification_status || (user.kyc?.status === 'verified' ? 'approved' : 
                              user.kyc?.status === 'rejected' ? 'rejected' : 
                              user.kyc?.status || 'pending')
        }
      }))

      setState(prev => ({
        ...prev,
        users: transformedUsers,
        loading: { ...prev.loading, users: false }
      }))
    } catch (error) {
      console.error('Error loading users:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load users',
        loading: { ...prev.loading, users: false }
      }))
    }
  }, [userId])

  // Load properties
  const loadProperties = useCallback(async () => {
    if (!userId) return

    setState(prev => ({ ...prev, loading: { ...prev.loading, properties: true }, error: null }))

    try {
      const token = localStorage.getItem('authToken')
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/properties`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error(`Failed to load properties: ${response.statusText}`)
      }
      const data = await response.json()

      // Transform properties to match frontend format
      const transformedProperties = (data.properties || []).map((property: any) => {
        // Build location string from address object if location is not a string
        let location = property.location
        if (!location && property.address && typeof property.address === 'object') {
          const addr = property.address
          const parts = []
          if (addr.street) parts.push(addr.street)
          if (addr.city) parts.push(addr.city)
          if (addr.state) parts.push(addr.state)
          if (parts.length > 0) {
            location = parts.join(', ')
          }
        }
        
        // Extract and normalize image URLs if images is an array of objects
        const imageUrls = property.images?.map((img: any) => typeof img === 'string' ? img : img.url) || property.images || [];
        const images = imageUrls.map((url: string) => {
          if (!url) return url;
          if (url.includes('localhost:5001') || url.includes('127.0.0.1:5001')) {
            const apiUrl = getApiUrl();
            const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
            try {
              const urlObj = new URL(url);
              return `${apiBaseUrl}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
            } catch {
              return url.replace(/https?:\/\/[^/]+/, apiBaseUrl);
            }
          } else if (url.startsWith('/uploads')) {
            const apiUrl = getApiUrl();
            const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
            return `${apiBaseUrl}${url}`;
          }
          return url;
        });
        
        // Get duration from leaseTerms for long-term rentals
        const duration = property.duration || property.leaseTerms?.minLease || null
        
        return {
          ...property,
          id: property._id || property.id,
          landlord_id: property.landlord?._id || property.landlord || property.landlord_id,
          // Add location if it was built from address
          location: location || property.location || 'Location not specified',
          // Ensure duration is available from leaseTerms for long-term rentals
          duration: duration,
          // Preserve leaseTerms for duration lookup
          leaseTerms: property.leaseTerms,
          // Transform images
          images: images,
          // Extract house documents URLs - handle both camelCase (houseDocuments) and snake_case (house_documents)
          house_documents: (() => {
            // First check if houseDocuments exists (backend format)
            if (property.houseDocuments && Array.isArray(property.houseDocuments) && property.houseDocuments.length > 0) {
              return property.houseDocuments.map((doc: any) => {
                if (typeof doc === 'string') return doc
                if (doc && typeof doc === 'object' && doc.url) return doc.url
                return doc
              })
            }
            // Fall back to house_documents if it exists (frontend format)
            if (property.house_documents && Array.isArray(property.house_documents) && property.house_documents.length > 0) {
              return property.house_documents.map((doc: any) => {
                if (typeof doc === 'string') return doc
                if (doc && typeof doc === 'object' && doc.url) return doc.url
                return doc
              })
            }
            return []
          })(),
          // Extract video URLs
          videos: property.videos?.map((video: any) => typeof video === 'string' ? video : video.url) || property.videos || [],
          // Map isVerified from backend (might be isVerified or is_verified)
          is_verified: property.isVerified !== undefined ? property.isVerified : property.is_verified,
          // Map isAvailable from backend
          is_available: property.isAvailable !== undefined ? property.isAvailable : property.is_available,
          // Map createdAt to created_at
          created_at: property.createdAt || property.created_at,
          // Transform landlord data
          landlord: property.landlord ? {
            ...property.landlord,
            id: property.landlord._id || property.landlord.id,
            is_verified: property.landlord.isVerified !== undefined ? property.landlord.isVerified : property.landlord.is_verified
          } : property.landlord
        }
      })

      setState(prev => ({
        ...prev,
        properties: transformedProperties,
        loading: { ...prev.loading, properties: false }
      }))
    } catch (error) {
      console.error('Error loading properties:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load properties',
        loading: { ...prev.loading, properties: false }
      }))
    }
  }, [userId])

  // Load applications
  const loadApplications = useCallback(async () => {
    if (!userId) return

    setState(prev => ({ ...prev, loading: { ...prev.loading, applications: true }, error: null }))

    try {
      const token = localStorage.getItem('authToken')
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/applications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error(`Failed to load applications: ${response.statusText}`)
      }
      const data = await response.json()

      // Transform applications to match frontend format
      const transformedApplications = (data.applications || []).map((application: any) => ({
        ...application,
        id: application._id || application.id
      }))

      setState(prev => ({
        ...prev,
        applications: transformedApplications,
        loading: { ...prev.loading, applications: false }
      }))
    } catch (error) {
      console.error('Error loading applications:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load applications',
        loading: { ...prev.loading, applications: false }
      }))
    }
  }, [userId])

  // Load payments
  const loadPayments = useCallback(async () => {
    if (!userId) return

    setState(prev => ({ ...prev, loading: { ...prev.loading, payments: true }, error: null }))

    try {
      const token = localStorage.getItem('authToken')
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/payments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error(`Failed to load payments: ${response.statusText}`)
      }
      const data = await response.json()

      // Transform payments to match frontend format
      const transformedPayments = (data.payments || []).map((payment: any) => ({
        ...payment,
        id: payment._id || payment.id
      }))

      setState(prev => ({
        ...prev,
        payments: transformedPayments,
        loading: { ...prev.loading, payments: false }
      }))
    } catch (error) {
      console.error('Error loading payments:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load payments',
        loading: { ...prev.loading, payments: false }
      }))
    }
  }, [userId])

  // Load payment summary (total revenue & platform commission from all completed payments)
  const loadPaymentSummary = useCallback(async () => {
    if (!userId) return

    setState(prev => ({ ...prev, loading: { ...prev.loading, paymentSummary: true }, error: null }))

    try {
      const token = localStorage.getItem('authToken')
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/stats/payment-summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error(`Failed to load payment summary: ${response.statusText}`)
      }
      const data = await response.json()

      setState(prev => ({
        ...prev,
        paymentSummary: {
          totalRevenue: data.totalRevenue ?? 0,
          platformCommission: data.platformCommission ?? 0,
        },
        loading: { ...prev.loading, paymentSummary: false },
      }))
    } catch (error) {
      console.error('Error loading payment summary:', error)
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, paymentSummary: false },
        paymentSummary: null,
      }))
    }
  }, [userId])

  // Calculate stats
  const calculateStats = useCallback(() => {
    const { users, properties, payments, paymentSummary } = state

    const totalUsers = users.length
    const totalProperties = properties.length
    const verifiedProperties = properties.filter(property => property.is_verified === true).length
    const pendingProperties = properties.filter(property => property.is_verified !== true).length
    
    // Check for KYC documents - support both kyc.documents (array) and kyc_data.documents (object)
    const hasKycDocuments = (user: any) => {
      if (user.kyc?.documents && user.kyc.documents.length > 0) return true
      if (user.kyc_data?.documents) {
        const docs = user.kyc_data.documents
        return typeof docs === 'object' && Object.keys(docs).length > 0
      }
      return false
    }
    
    const kycApplications = users.filter(user => hasKycDocuments(user) && user.role !== 'admin').length
    const pendingKycApplications = users.filter(user => hasKycDocuments(user) && !user.is_verified && user.role !== 'admin').length
    const approvedKycApplications = users.filter(user => hasKycDocuments(user) && user.is_verified && user.role !== 'admin').length
    const verifiedUsers = users.filter(user => user.is_verified).length
    const unverifiedUsers = users.filter(user => !user.is_verified).length
    const landlords = users.filter(user => user.role === 'landlord').length
    const clients = users.filter(user => user.role === 'client').length

    // Use server-side payment summary when available (all completed payments); otherwise fall back to paginated payments
    const totalRevenue = paymentSummary ? paymentSummary.totalRevenue : payments.reduce((sum, p) => sum + (p.amount || 0), 0)
    const platformCommission = paymentSummary ? paymentSummary.platformCommission : payments.reduce((sum, p) => sum + (p.commission_amount || 0), 0)
    const totalCommission = platformCommission

    const verificationRate = totalUsers > 0 ? Math.round((verifiedUsers / totalUsers) * 100) : 0
    const propertyVerificationRate = totalProperties > 0 ? Math.round((verifiedProperties / totalProperties) * 100) : 0

    const stats: AdminStats = {
      totalUsers,
      totalProperties,
      verifiedProperties,
      pendingProperties,
      kycApplications,
      pendingKycApplications,
      approvedKycApplications,
      verifiedUsers,
      unverifiedUsers,
      landlords,
      clients,
      totalRevenue,
      totalCommission,
      platformCommission,
      verificationRate,
      propertyVerificationRate,
    }

    setState(prev => ({ ...prev, stats }))
  // }, [state])
  },  [state.users, state.properties, state.payments])

  // Update user verification
  const updateUserVerification = useCallback(async (userId: string, isVerified: boolean) => {
    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('No auth token found')

      // Backend: KYC approval drives user.isVerified
      const response = await fetch(`${apiUrl}/users/admin/${userId}/kyc`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: isVerified ? 'verified' : 'rejected',
          rejectedReason: isVerified ? undefined : 'Not verified'
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to update user verification: ${response.statusText}`)
      }

      // Reload users after update
      await loadUsers()
    } catch (error) {
      console.error('Error updating user verification:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update user verification'
      }))
      throw error
    }
  }, [loadUsers])

  // Update property verification
  const updatePropertyVerification = useCallback(async (propertyId: string, isVerified: boolean) => {
    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('No auth token found')

      const response = await fetch(`${apiUrl}/admin/properties/${propertyId}/verify`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isVerified })
      })

      if (!response.ok) {
        throw new Error(`Failed to update property verification: ${response.statusText}`)
      }

      // Reload properties after update
      await loadProperties()
    } catch (error) {
      console.error('Error updating property verification:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update property verification'
      }))
      throw error
    }
  }, [loadProperties])

  // Update property availability
  const updatePropertyAvailability = useCallback(async (propertyId: string, isAvailable: boolean) => {
    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('No auth token found')

      const response = await fetch(`${apiUrl}/admin/properties/${propertyId}/availability`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isAvailable })
      })

      if (!response.ok) {
        throw new Error(`Failed to update property availability: ${response.statusText}`)
      }

      // Reload properties after update
      await loadProperties()
    } catch (error) {
      console.error('Error updating property availability:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update property availability'
      }))
      throw error
    }
  }, [loadProperties])

  // Delete user
  const deleteUser = useCallback(async (userId: string) => {
    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('No auth token found')
      const response = await fetch(`${apiUrl}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to delete user: ${response.statusText}`)
      }

      // Reload users after deletion
      await loadUsers()
    } catch (error) {
      console.error('Error deleting user:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to delete user'
      }))
      throw error
    }
  }, [loadUsers])

  // Delete property
  const deleteProperty = useCallback(async (propertyId: string) => {
    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('No auth token found')

      // Backend: property deletion is handled by `/api/properties/:id` (admin allowed).
      const response = await fetch(`${apiUrl}/properties/${propertyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to delete property: ${response.statusText}`)
      }

      // Reload properties after deletion
      await loadProperties()
    } catch (error) {
      console.error('Error deleting property:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to delete property'
      }))
      throw error
    }
  }, [loadProperties])

  // Load all data
  const loadAllData = useCallback(async () => {
    if (!userId) return

    try {
      await Promise.all([
        loadUsers(),
        loadProperties(),
        loadApplications(),
        loadPayments(),
        loadPaymentSummary(),
      ])
    } catch (error) {
      console.error('Error loading all data:', error)
      setState(prev => ({
        ...prev,
        error: 'Failed to load data. Please try again.'
      }))
    }
  }, [userId, loadUsers, loadProperties, loadApplications, loadPayments, loadPaymentSummary])

  // Load data on mount and when userId changes
  useEffect(() => {
    if (userId) {
      loadAllData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]) // Only depend on userId - loadAllData is stable due to useCallback

  // Calculate stats when data changes
  useEffect(() => {
    calculateStats()
  }, [state.users, state.properties, state.payments, state.paymentSummary, calculateStats])

  return {
    // State
    users: state.users,
    properties: state.properties,
    applications: state.applications,
    payments: state.payments,
    loading: state.loading,
    error: state.error,
    stats: state.stats,

    // Actions
    loadUsers,
    loadProperties,
    loadApplications,
    loadPayments,
    loadAllData,
    updateUserVerification,
    updatePropertyVerification,
    updatePropertyAvailability,
    deleteUser,
    deleteProperty,

    // Utilities
    refreshAll: loadAllData
  }
}
